import { useEffect, useMemo } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { TextField, Autocomplete, ListItem } from '@mui/material'
import { PropertyFieldProps } from './PropertyField'
import { getValueByPath } from './PropertySelectFromForeignKey'
import useGetDataFromForeignKeyAndDependencies from './useGetDataFromForeignKeyAndDependencies'

type PropertyMultiSelectFromForeignKeyProps = PropertyFieldProps

export default function PropertyMultiSelectFromForeignKey({
  propertyId,
  property,
  required,
  shrinkLabel,
  variant,
  dependencyValues,
}: PropertyMultiSelectFromForeignKeyProps) {
  const { field: formField } = useController({
    name: propertyId,
    rules: { required },
  })
  const { formState, resetField, setValue } = useFormContext()
  const { errors } = formState

  const { display, foreignKey } = property

  const {
    allData: data,
    isPending,
    disabledIds,
    hasAllDependencies,
  } = useGetDataFromForeignKeyAndDependencies({
    foreignKey,
    dependencyValues,
  })

  const currentValues = useWatch({ name: propertyId })
  const namePath = display.item?.name_path

  const values = useMemo(
    () => data.filter(({ id }) => currentValues.includes(id)),
    [data, currentValues],
  )

  const existsInData = formField.value.length
    ? data.some(
        ({ id }) => formField.value.includes(id) && !disabledIds.includes(id),
      )
    : true

  useEffect(() => {
    // If the selected item no longer exists (i.e. the data set changes), reset the value
    if (data?.length > 0 && !existsInData) {
      resetField(propertyId, { defaultValue: [] })
    }
  }, [data, propertyId, existsInData, resetField])

  return (
    <Autocomplete
      {...formField}
      disabled={isPending || !hasAllDependencies}
      disableCloseOnSelect
      value={values}
      multiple
      options={data}
      loading={isPending}
      getOptionLabel={(option) =>
        namePath ? getValueByPath(option, namePath) : option.name || option.code
      }
      getOptionDisabled={(option) => disabledIds.includes(option.id)}
      onChange={(_e, values) => {
        const ids = values.map(({ id }) => id)
        setValue(propertyId, ids, { shouldDirty: true })
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          error={!!errors[propertyId]}
          label={display?.label || 'MISSING LABEL'}
          helperText={display.description}
          variant={variant}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              required: required ? values.length === 0 : false, // workaround to allow submitting form https://github.com/mui/material-ui/issues/21663#issuecomment-732298594
            },
            inputLabel: { shrink: shrinkLabel },
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <ListItem {...props} key={option.id} dense>
            {namePath
              ? getValueByPath(option, namePath)
              : option.name || option.code}
          </ListItem>
        )
      }}
    />
  )
}
